import React, { useState, useEffect, useCallback } from "react";
import {
  listNotificationTemplates,
  listNotificationTemplatesPortal,
  sendNotificationToStudent,
  sendNotificationToStudentPortal,
  sendReceiptNotification,
  sendReceiptNotificationPortal,
} from "../../api/fees/feeApi";
import {
  Modal,
  Button,
  Select,
  Badge,
  Alert,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const EVENT_OPTIONS = [
  { value: "DUE_REMINDER", label: "Due Reminder" },
  { value: "OVERDUE_REMINDER", label: "Overdue Reminder" },
  { value: "RECEIPT", label: "Receipt" },
  { value: "CUSTOM", label: "Custom" },
];

const CHANNEL_OPTIONS = [
  { value: "", label: "All enabled channels" },
  { value: "WHATSAPP", label: "WhatsApp only" },
  { value: "SMS", label: "SMS only" },
  { value: "EMAIL", label: "Email only" },
];

const FeeSendNotificationModal = ({
  isOpen,
  onClose,
  studentId,
  receiptId = null,
  isPortal = false,
  defaultEventType = "DUE_REMINDER",
  onSuccess,
}) => {
  const [eventType, setEventType] = useState(defaultEventType);
  const [channel, setChannel] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const toast = useToast();

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const params = { eventType, isActive: "true" };
      if (channel) params.channel = channel;
      const res = isPortal
        ? await listNotificationTemplatesPortal(params)
        : await listNotificationTemplates(params);
      setTemplates(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load templates.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [eventType, channel, isPortal]);

  useEffect(() => {
    if (!isOpen) return;
    setEventType(receiptId ? "RECEIPT" : defaultEventType);
    setResults(null);
  }, [isOpen, defaultEventType, receiptId]);

  useEffect(() => {
    if (!isOpen) return;
    fetchTemplates();
  }, [isOpen, fetchTemplates]);

  const handleSend = async () => {
    setSending(true);
    setResults(null);
    try {
      let res;
      if (receiptId) {
        const payload = { receiptId };
        if (channel) payload.channel = channel;
        res = isPortal
          ? await sendReceiptNotificationPortal(payload)
          : await sendReceiptNotification(payload);
      } else {
        const payload = { studentId, eventType };
        if (channel) payload.channel = channel;
        res = isPortal
          ? await sendNotificationToStudentPortal(payload)
          : await sendNotificationToStudent(payload);
      }
      setResults(res.data || []);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send notification.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Notification"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={sending}
          >
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={sending}
            disabled={templates.length === 0}
            onClick={handleSend}
          >
            Send Now
          </Button>
        </>
      }
    >
      <div className="space-y-4 pt-1">
        

        {receiptId ? (
          <Alert variant="info" title="Payment Receipt Notification">
            Sending a <strong>Receipt</strong> notification for this payment.
          </Alert>
        ) : (
          <Select
            label="Event"
            options={EVENT_OPTIONS}
            value={eventType}
            onChange={(val) => setEventType(val)}
          />
        )}

        <Select
          label="Channel"
          options={CHANNEL_OPTIONS}
          value={channel}
          onChange={(val) => setChannel(val)}
        />

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-700 dark:text-neutral-200">
              Template Preview
            </span>
            <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
              Structural Preview
            </span>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Placeholders are filled in automatically when the message is sent.
          </p>

          {loadingTemplates ? (
            <div className="space-y-2 rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-3">
              <Skeleton height={14} width="40%" />
              <Skeleton height={12} width="100%" />
              <Skeleton height={12} width="80%" />
            </div>
          ) : templates.length === 0 ? (
            <Alert variant="warning" title="No Template Available">
              No active template found for this event/channel. Add one in Notification Templates first.
            </Alert>
          ) : (
            <div className="max-h-48 space-y-2 overflow-y-auto pr-0.5">
              {templates.map((tpl) => (
                <div
                  key={tpl._id}
                  className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-3 text-xs"
                >
                  <div className="mb-1.5 flex items-center justify-between border-b border-neutral-200/60 pb-1 font-medium text-neutral-600 dark:text-neutral-300">
                    <span>{tpl.name}</span>
                    <Badge variant="primary">{tpl.channel}</Badge>
                  </div>
                  <p className="whitespace-pre-wrap font-mono text-[11px] text-neutral-800 dark:text-neutral-100">
                    {tpl.body}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {results && (
          <div className="space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-700 dark:text-neutral-200">
              Send Results
            </span>
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {results.map((r, i) => (
                <div key={i}>
                  <Alert
                    variant={r.skipped ? "warning" : "success"}
                    title={`${r.channel}`}
                  >
                    {r.skipped ? r.reason : "Queued for delivery"}
                  </Alert>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FeeSendNotificationModal;